import { Link } from "react-router-dom";

import { DEFAULT_PAGE_SIZE } from "constants/Constants";
import { startCase } from "helpers/string";

export const MODEL_ROUTES = {
  Campaign: "campaigns",
  CampaignTarget: "campaigns/{campaign_id}/target",
  Entity: "entities",
  Contact: "contacts",
  Case: "cases",
  CampaignCase: "campaigns/{campaign_id}/cases",
  RevenueOpportunity: "revenue_opportunities",
};

export function parseCustomTableAuditableType(auditableType) {
  if (typeof auditableType !== "string") return null;
  const [type, slug] = auditableType.split(":", 2);
  return type === "CustomTable" && slug ? slug : null;
}

export function getModelLink(
  model_id,
  model_type,
  { campaign_id = "", label = "", machine_id, case_id } = {}
) {
  // model_type is nullable on the API: it comes from an outer join on the
  // activity log, so an audit entry whose activity log doesn't join has none.
  if (!model_type) {
    return <>{label}</>;
  }

  if (model_type.toLowerCase() === "machine") {
    return getMachineModelLink(case_id, machine_id);
  }

  const ctSlug = parseCustomTableAuditableType(model_type);
  if (ctSlug) {
    return (
      <Link to={`/custom_tables/${ctSlug}/${model_id}`}>
        {label || startCase(ctSlug)}
      </Link>
    );
  }

  const route = MODEL_ROUTES[model_type];
  // The listener emits creation logs for models that have no page of their own;
  // show the label rather than throwing on a missing route.
  if (!route) {
    return <>{label || startCase(model_type)}</>;
  }

  const path = route.replace("{campaign_id}", campaign_id);
  return (
    <Link to={`/${path}/${model_id}`}>{label || startCase(model_type)}</Link>
  );
}

export function getMachineModelLink(caseId, machineId) {
  return (
    <Link
      to={`/cases/${caseId}/machines?page=1&per_page=${DEFAULT_PAGE_SIZE}&machine_id=${machineId}`}
    >
      Machine ID: ({machineId})
    </Link>
  );
}

export function getMachinesPageLink(machineId) {
  return (
    <Link
      to={`/machines?page=1&per_page=${DEFAULT_PAGE_SIZE}&machine_id=${machineId}`}
    >
      Machine ID: ({machineId})
    </Link>
  );
}

export function resolveModelDisplay({
  revenue_opportunity_id = null,
  auditable_type = null,
  auditable_id = null,
  custom_table_singular_name = null,
  machine_id = null,
  case_id = null,
  case_name = null,
  contact_id = null,
  contact_name = null,
  entity_id = null,
  entity_name = null,
  campaign_name = null,
}) {
  const ctSlug = parseCustomTableAuditableType(auditable_type);
  if (auditable_type === "Campaign") {
    return (
      <>
        campaign{" "}
        {getModelLink(auditable_id, "Campaign", { label: campaign_name })}
      </>
    );
  }
  if (revenue_opportunity_id) {
    return <span>Revenue Opportunity (ID: {revenue_opportunity_id})</span>;
  }
  if (ctSlug) {
    return (
      <>
        {getModelLink(auditable_id, auditable_type, {
          label: custom_table_singular_name || startCase(ctSlug),
        })}
      </>
    );
  }
  if (machine_id) {
    return (
      <>
        {" "}
        {getModelLink(case_id, "Machine", {
          label: machine_id,
          machine_id,
          case_id,
        })}
      </>
    );
  }
  if (contact_id) {
    return (
      <>
        contact {getModelLink(contact_id, "Contact", { label: contact_name })}
      </>
    );
  }
  if (case_id) {
    return <>case {getModelLink(case_id, "Case", { label: case_name })}</>;
  }
  // Campaign-level records (dial sessions, schedules) belong to no entity.
  if (!entity_id) {
    return null;
  }
  return (
    <>entity {getModelLink(entity_id, "Entity", { label: entity_name })}</>
  );
}
